import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, ChevronDown, ChevronRight, FileText } from "lucide-react";

interface PageContentRow {
  id: string;
  slug: string;
  page_title: string;
  content: Record<string, any>;
  status: string;
  updated_at: string;
}

const ContentEditor = () => {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<Record<string, any>>({});
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: pages, isLoading } = useQuery({
    queryKey: ["admin-page-content"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("*")
        .order("page_title");
      if (error) throw error;
      return data as PageContentRow[];
    },
  });

  const selectedPage = pages?.find((p) => p.slug === selectedSlug);

  useEffect(() => {
    if (selectedPage) {
      setEditedContent(selectedPage.content);
      setExpandedSections([]);
    }
  }, [selectedSlug, selectedPage?.updated_at]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPage) return;
      const { error } = await supabase
        .from("page_content")
        .update({ content: editedContent })
        .eq("id", selectedPage.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-page-content"] });
      queryClient.invalidateQueries({ queryKey: ["page-content", selectedSlug] });
      toast.success("Content saved successfully");
    },
    onError: (err: any) => {
      toast.error("Failed to save: " + err.message);
    },
  });

  const toggleSection = (key: string) => {
    setExpandedSections((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const updateField = (path: string, value: any) => {
    setEditedContent((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = isNaN(Number(keys[i])) ? keys[i] : Number(keys[i]);
        obj = obj[key];
      }
      const lastKey = isNaN(Number(keys[keys.length - 1]))
        ? keys[keys.length - 1]
        : Number(keys[keys.length - 1]);
      obj[lastKey] = value;
      return copy;
    });
  };

  const renderField = (key: string, value: any, path: string) => {
    if (typeof value === "string") {
      const isLong = value.length > 100;
      return (
        <div key={path} className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {key}
          </label>
          {isLong ? (
            <Textarea
              value={value}
              onChange={(e) => updateField(path, e.target.value)}
              rows={3}
              className="text-sm"
            />
          ) : (
            <Input
              value={value}
              onChange={(e) => updateField(path, e.target.value)}
              className="text-sm"
            />
          )}
        </div>
      );
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return null;

      // Array of strings
      if (typeof value[0] === "string") {
        return (
          <div key={path} className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {key}
            </label>
            {value.map((item, i) => (
              <Input
                key={`${path}.${i}`}
                value={item}
                onChange={(e) => updateField(`${path}.${i}`, e.target.value)}
                className="text-sm"
              />
            ))}
          </div>
        );
      }

      // Array of objects
      return (
        <div key={path} className="space-y-3">
          <button
            onClick={() => toggleSection(path)}
            className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
          >
            {expandedSections.includes(path) ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {key} ({value.length} items)
          </button>
          {expandedSections.includes(path) && (
            <div className="ml-4 space-y-4 border-l border-border pl-4">
              {value.map((item, i) => (
                <div key={`${path}.${i}`} className="space-y-2 bg-muted/30 rounded-lg p-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    {item.name || item.title || item.question || `Item ${i + 1}`}
                  </span>
                  {Object.entries(item).map(([k, v]) => {
                    if (k === "icon") return null; // Skip icon fields (resolved at render)
                    return renderField(k, v, `${path}.${i}.${k}`);
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (typeof value === "object" && value !== null) {
      return (
        <div key={path} className="space-y-3">
          <button
            onClick={() => toggleSection(path)}
            className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
          >
            {expandedSections.includes(path) ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {key}
          </button>
          {expandedSections.includes(path) && (
            <div className="ml-4 space-y-3 border-l border-border pl-4">
              {Object.entries(value).map(([k, v]) =>
                renderField(k, v, `${path}.${k}`)
              )}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // Top-level content fields organized into logical groups
  const sectionGroups: Record<string, string[]> = {
    "SEO & Meta": ["seoTitle", "seoDescription", "canonicalUrl"],
    "Hero": ["category", "categoryLabel", "headline", "subheadline", "ctaText"],
    "Problem": ["problemTitle", "problems"],
    "System": ["systemTitle", "systemDescription"],
    "Services": ["services"],
    "Process": ["processTitle", "processSteps"],
    "Technology": ["techTitle", "techDescription", "technologyCategories"],
    "Qualification": ["qualification"],
    "FAQs": ["faqs"],
    "Final CTA": ["finalCtaTitle", "finalCtaDescription", "finalCtaButtonText"],
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Content Editor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Edit page content without code changes
          </p>
        </div>
        {selectedPage && (
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Page list */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-card border border-border rounded-lg p-2 space-y-1">
            {isLoading ? (
              <p className="text-sm text-muted-foreground p-2">Loading...</p>
            ) : pages?.length === 0 ? (
              <p className="text-sm text-muted-foreground p-2">No pages configured</p>
            ) : (
              pages?.map((page) => (
                <button
                  key={page.slug}
                  onClick={() => setSelectedSlug(page.slug)}
                  className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedSlug === page.slug
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-accent"
                  }`}
                >
                  <FileText className="h-4 w-4 flex-shrink-0" />
                  {page.page_title}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 min-w-0">
          {!selectedSlug ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <p className="text-muted-foreground">Select a page to edit its content</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-6 space-y-6">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <h2 className="text-lg font-semibold">{selectedPage?.page_title}</h2>
                <span className="text-xs text-muted-foreground">
                  /{selectedPage?.slug}
                </span>
              </div>

              {Object.entries(sectionGroups).map(([groupName, fields]) => {
                const groupPath = `group-${groupName}`;
                const hasContent = fields.some((f) => editedContent[f] !== undefined);
                if (!hasContent) return null;

                return (
                  <div key={groupName} className="space-y-3">
                    <button
                      onClick={() => toggleSection(groupPath)}
                      className="flex items-center gap-2 text-sm font-semibold text-foreground uppercase tracking-wide hover:text-primary transition-colors"
                    >
                      {expandedSections.includes(groupPath) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      {groupName}
                    </button>
                    {expandedSections.includes(groupPath) && (
                      <div className="ml-4 space-y-4 border-l border-border pl-4">
                        {fields.map((field) => {
                          if (editedContent[field] === undefined) return null;
                          return renderField(field, editedContent[field], field);
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;
